import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button, cx } from "../../ui";
import type { ActivityItem, Entry } from "./activity-model";
import { ActivityRow } from "./ActivityRow";
import { accountColour, relativeDate, senderAddress, senders, type Thread } from "./model";
import { snoozeLabel } from "./snooze-model";

/**
 * The conversation list, virtualised against the window (long inboxes stay fast; the page
 * scrolls normally on a phone). Rows are buttons; the cursor row is marked `aria-current` and
 * takes focus when the keyboard moves it. Checkboxes show only while selecting (the filter bar's
 * Select button, or once anything is selected), so rows keep their width for text; shift-click
 * selects a range. Each row's left edge is a thin bar in its mailbox's colour. A snoozed row also
 * has "Unsnooze now". Chats and tasks (WP-054) sit among the conversations as their own rows; they
 * take the cursor and open like a conversation, but have no checkbox.
 */
const ROW_HEIGHT = 74;

export interface AccountInfo {
  address: string;
  /** Chosen in Settings → Accounts; null: automatic. */
  color: string | null;
}

/** How long a pointer rests on a row before its conversation is read ahead. */
export const REST_MS = 150;

export function ThreadList({
  entries,
  unread,
  nameOf,
  accounts,
  cursor,
  focusCursor,
  now,
  hasMore,
  loadingMore,
  onLoadMore,
  onOpen,
  onOpenActivity,
  onPrefetch,
  onCursor,
  selected,
  onSelect,
  onSelectAll,
  onUnsnooze,
  selecting,
  onDoneSelecting,
  sent = false,
}: {
  entries: readonly Entry[];
  /** Whether a chat or task row is unread (its own read time, or Chat's read marker). */
  unread: (item: ActivityItem) => boolean;
  nameOf: (uid: string) => string;
  accounts: ReadonlyMap<string, AccountInfo>;
  cursor: number;
  /** Bumped when the keyboard moved the cursor, so focus follows it. */
  focusCursor: number;
  now: Date;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  onOpen: (thread: Thread) => void;
  onOpenActivity: (item: ActivityItem) => void;
  /** Start reading a conversation the person is about to open (pointer resting, keyboard on it). */
  onPrefetch?: (thread: Thread) => void;
  onCursor: (index: number) => void;
  selected: ReadonlySet<string>;
  onSelect: (index: number, shift: boolean) => void;
  onSelectAll: () => void;
  /** Wakes a snoozed conversation now (the Snoozed view's per-row action). */
  onUnsnooze: (thread: Thread) => void;
  /** Bulk selection mode: checkboxes on every row, and a select-all row with "Done". */
  selecting: boolean;
  onDoneSelecting: () => void;
  /** The Sent view (WP-055): rows name who the mail went to and when the mailbox last sent. */
  sent?: boolean;
}) {
  const listRef = useRef<HTMLUListElement>(null);
  const [offset, setOffset] = useState(0);
  useLayoutEffect(() => {
    if (listRef.current !== null) setOffset(listRef.current.getBoundingClientRect().top + window.scrollY);
  }, []);

  const virtualizer = useWindowVirtualizer({
    count: entries.length,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
    scrollMargin: offset,
    initialRect: { width: 800, height: 900 },
  });
  const rows = useRef(new Map<number, HTMLButtonElement>());

  useEffect(() => {
    if (focusCursor === 0) return;
    virtualizer.scrollToIndex(cursor, { align: "auto" });
    const frame = requestAnimationFrame(() => rows.current.get(cursor)?.focus());
    return () => cancelAnimationFrame(frame);
  }, [focusCursor, cursor, virtualizer]);

  const items = virtualizer.getVirtualItems();
  const last = items[items.length - 1];
  useEffect(() => {
    if (last !== undefined && last.index >= entries.length - 5 && hasMore && !loadingMore) onLoadMore();
  }, [last, entries.length, hasMore, loadingMore, onLoadMore]);

  const threads = entries.flatMap((e) => (e.kind === "thread" ? [e.thread] : []));
  const allSelected = threads.length > 0 && threads.every((t) => selected.has(t.id));
  // Chat and task rows share the selection's order (so indexes line up) but are never counted.
  const selectedCount = threads.filter((t) => selected.has(t.id)).length;
  return (
    <div>
      {selecting ? <SelectAllBar allSelected={allSelected} selectedCount={selectedCount} onSelectAll={onSelectAll} onDone={onDoneSelecting} /> : null}
      <ul ref={listRef} aria-label="Conversations" className="relative" style={{ height: virtualizer.getTotalSize() }}>
        {items.map((item) => {
          const entry = entries[item.index];
          if (entry === undefined) return null;
          const place = { transform: `translateY(${item.start - virtualizer.options.scrollMargin}px)`, height: ROW_HEIGHT };
          const rowRef = (el: HTMLButtonElement | null) => {
            if (el === null) rows.current.delete(item.index);
            else rows.current.set(item.index, el);
          };
          if (entry.kind === "activity") {
            const a = entry.item;
            return (
              <li key={a.id} data-index={item.index} className="absolute inset-x-0 top-0" style={place}>
                <div className="relative flex h-full items-stretch">
                  <ActivityRow
                    item={a}
                    unread={unread(a)}
                    actor={nameOf(a.actorUid)}
                    now={now}
                    current={item.index === cursor}
                    rowRef={rowRef}
                    onFocus={() => onCursor(item.index)}
                    onOpen={() => onOpenActivity(a)}
                  />
                </div>
              </li>
            );
          }
          const t = entry.thread;
          return (
            <li key={t.id} data-index={item.index} className="absolute inset-x-0 top-0" style={place}>
              <div className={cx("relative flex h-full items-stretch", selected.has(t.id) && "bg-surface-2")}>
                <span aria-hidden="true" className="absolute inset-y-1.5 left-0 w-[3px] rounded-r" style={{ backgroundColor: accountColour(t.accountId, colorOf(accounts.get(t.accountId))) }} />
                {selecting ? (
                  <SelectBox label={`Select ${t.subject === "" ? "(no subject)" : t.subject}`} checked={selected.has(t.id)} onToggle={(shift) => onSelect(item.index, shift)} />
                ) : null}
                <ThreadRow
                  thread={t}
                  account={accounts.get(t.accountId)}
                  now={now}
                  sent={sent}
                  current={item.index === cursor}
                  rowRef={rowRef}
                  onFocus={() => {
                    onCursor(item.index);
                    onPrefetch?.(t);
                  }}
                  onOpen={() => onOpen(t)}
                  onRest={onPrefetch === undefined ? undefined : () => onPrefetch(t)}
                />
                {t.snoozedUntil === null ? null : <UnsnoozeTail thread={t} onUnsnooze={onUnsnooze} />}
              </div>
            </li>
          );
        })}
      </ul>
      {hasMore ? (
        <div className="flex justify-center p-3">
          <Button onClick={onLoadMore} disabled={loadingMore}>
            {loadingMore ? "Loading…" : "Load more"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** While selecting: select every loaded conversation, the count, and Done. */
function SelectAllBar({ allSelected, selectedCount, onSelectAll, onDone }: { allSelected: boolean; selectedCount: number; onSelectAll: () => void; onDone: () => void }) {
  return (
    <div className="flex min-h-9 items-center gap-3 border-b border-border px-3">
          <label className="flex flex-1 cursor-pointer items-center gap-3">
            <input
              type="checkbox"
              aria-label="Select all loaded conversations"
              checked={allSelected}
              onChange={onSelectAll}
              className="size-4 shrink-0 accent-accent"
            />
            <span className="text-xs text-muted">{selectedCount > 0 ? `${selectedCount} selected` : "Select conversations"}</span>
          </label>
          <Button variant="ghost" className="min-h-7 px-2 text-xs" onClick={onDone}>
            Done
          </Button>
        </div>
  );
}

const colorOf = (a: AccountInfo | undefined) => (a === undefined ? null : a.color);

/** Beside a snoozed row: "Unsnooze now". */
function UnsnoozeTail({ thread, onUnsnooze }: { thread: Thread; onUnsnooze: (thread: Thread) => void }) {
  return (
    <div className="flex shrink-0 items-center border-b border-border pr-2">
      <Button variant="ghost" aria-label={`Unsnooze now: ${thread.subject === "" ? "(no subject)" : thread.subject}`} onClick={() => onUnsnooze(thread)}>
        Unsnooze now
      </Button>
    </div>
  );
}

/** A row's checkbox: a click (or Space) toggles it; with shift held, the range from the last one. */
export function SelectBox({ label, checked, onToggle }: { label: string; checked: boolean; onToggle: (shift: boolean) => void }) {
  return (
    <label className="flex shrink-0 cursor-pointer items-start border-b border-border py-2 pl-3 pr-2">
      <input
        type="checkbox"
        aria-label={label}
        checked={checked}
        onChange={() => undefined}
        onClick={(e) => onToggle(e.shiftKey)}
        className="mt-1 size-4 accent-accent"
      />
    </label>
  );
}

export function ThreadRow({
  thread,
  account,
  now,
  current,
  rowRef,
  onFocus,
  onOpen,
  onRest,
  sent = false,
}: {
  thread: Thread;
  account: AccountInfo | undefined;
  now: Date;
  /** In the Sent view: "To:" before the names, and the time of the newest sent message. */
  sent?: boolean;
  current: boolean;
  rowRef: (el: HTMLButtonElement | null) => void;
  onFocus: () => void;
  onOpen: () => void;
  /** The pointer rested on the row for `REST_MS` (a pointer passing over it does not count). */
  onRest: (() => void) | undefined;
}) {
  const rest = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelRest = () => {
    if (rest.current !== null) clearTimeout(rest.current);
    rest.current = null;
  };
  useEffect(() => cancelRest, []);
  const address = account === undefined ? null : account.address;
  const who = senders(thread.participants, address);
  const from = senderAddress(thread.participants, address);
  const sentAt = sent ? thread.sentAt : null;
  const at = sentAt === null ? thread.lastMessageAt : sentAt;
  const youLast = address !== null && thread.lastFrom !== null && thread.lastFrom.email.toLowerCase() === address.toLowerCase();
  return (
    <button
      ref={rowRef}
      type="button"
      data-thread-id={thread.id}
      aria-current={current ? "true" : undefined}
      onFocus={onFocus}
      onClick={onOpen}
      onPointerEnter={
        onRest === undefined
          ? undefined
          : () => {
              cancelRest();
              rest.current = setTimeout(onRest, REST_MS);
            }
      }
      onPointerLeave={cancelRest}
      // A tap or press starts the read before the click lands.
      onPointerDown={onRest}
      className={cx(
        "flex h-full min-w-0 flex-1 items-start gap-3 border-b border-border px-3 py-2 text-left hover:bg-surface-2",
        current && "bg-surface-2",
      )}
    >
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-baseline gap-2">
          {/* The name gives way first: the address keeps its width unless the row is narrower than it. */}
          <span className={cx("min-w-8 shrink-[999] truncate text-sm", thread.unread ? "font-semibold text-text" : "text-text")}>
            {thread.unread ? <span className="sr-only">Unread, </span> : null}
            {sentAt === null ? null : <span className="font-normal text-muted">To: </span>}
            {who}
            {thread.messageIds.length > 1 ? <span className="ml-1 text-xs font-normal text-muted">{thread.messageIds.length}</span> : null}
          </span>
          <span className="min-w-0 truncate text-xs text-muted">{from}</span>
          <span className="flex-1" />
          {thread.starred ? (
            <span className="text-xs text-accent">
              <span aria-hidden="true">★</span>
              <span className="sr-only">Starred</span>
            </span>
          ) : null}
          {thread.hasAttachments ? <PaperclipIcon /> : null}
          {thread.snoozedUntil === null ? (
            <time dateTime={at.toISOString()} className={cx("shrink-0 text-xs", thread.unread ? "font-semibold text-text" : "text-muted")}>
              {sentAt === null ? null : <span className="sr-only">Sent </span>}
              {relativeDate(at, now)}
            </time>
          ) : (
            <time dateTime={thread.snoozedUntil.toISOString()} className="shrink-0 text-xs text-accent">
              Snoozed until {snoozeLabel(thread.snoozedUntil)}
            </time>
          )}
        </span>
        <span className="flex min-w-0 items-center gap-1.5">
          <span className={cx("min-w-0 truncate text-sm", thread.unread ? "font-semibold text-text" : "text-text")}>
            {thread.subject === "" ? "(no subject)" : thread.subject}
          </span>
          {thread.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="shrink-0 rounded border border-border px-1.5 text-xs text-muted">
              {tag}
            </span>
          ))}
        </span>
        {/* The newest words, with "You:" when the mailbox sent the last message (replies "A"). */}
        <span className="min-w-0 truncate text-xs text-muted">
          {youLast ? <span className="font-semibold text-accent">You: </span> : null}
          {thread.snippet}
        </span>
        {address === null ? null : <span className="sr-only">Account {address}</span>}
      </span>
    </button>
  );
}

function PaperclipIcon() {
  return (
    <span className="shrink-0 text-muted">
      <svg aria-hidden="true" viewBox="0 0 20 20" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M14.5 9.5 9 15a3.5 3.5 0 0 1-5-5l6.5-6.5a2.3 2.3 0 0 1 3.3 3.3L7.4 13.2a1.2 1.2 0 0 1-1.7-1.7L11 6.2" />
      </svg>
      <span className="sr-only">Has attachment</span>
    </span>
  );
}
